import React, { useEffect, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Divider from '@material-ui/core/Divider';
import { showAlert } from 'actions/app';
import { StatusManager } from 'common/components/StatusManager';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { Loader } from 'features/command-center/components/shared/Widget/Loader';
import strings from 'strings';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { UIStatus } from 'UI/constants/status';

import { DetailsOverview } from './components/shared';
import { ClosingCall, DebriefingCalls, Notes, PreparationCalls } from './components';
import { DRAWER_TITLES_MAP, DRAWER_VARIANTS_MAP } from './InterviewDetailsDrawer.constants';
import { useInterviewDetailsHook } from './InterviewDetailsDrawer.hook';
import { formatDefaultValues } from './utils';

/**
 * @typedef {Object} InterviewDetailsDrawerProps
 * @property {boolean} open
 * @property {Function} onClose
 * @property {DrawerVariant} drawerVariant
 * @property {number} [interviewId]
 * @property {Function} onSubmit
 * @property {Function} setDrawerVariant
 */

const getContentToRender = ({ drawerVariant }) => {
  const CONTENT_MAP = {
    [DRAWER_VARIANTS_MAP.preparation]: <PreparationCalls />,
    [DRAWER_VARIANTS_MAP.debriefing]: <DebriefingCalls />,
    [DRAWER_VARIANTS_MAP.closingCall]: <ClosingCall />,
    [DRAWER_VARIANTS_MAP.notes]: <Notes />,
    DEFAULT: null
  };

  return CONTENT_MAP[drawerVariant] || CONTENT_MAP.DEFAULT;
};

/**
 * @param {InterviewDetailsDrawerProps} props
 */
export const InterviewDetailsDrawer = ({
  open,
  onClose,
  drawerVariant,
  interviewId,
  onSubmit,
  setDrawerVariant
}) => {
  const [isSaving, setIsSaving] = useState(false);

  const dispatch = useDispatch();

  const { detailsOverviewFetchState, formFetchState, existingItemId, DetailStatus } =
    useInterviewDetailsHook({
      drawerVariant,
      interviewId
    });

  const ACTION_VERB = existingItemId ? 'updated' : 'created';

  const onSave = async formData => {
    setIsSaving(true);
    const response = await onSubmit(formData, interviewId, existingItemId);
    setIsSaving(false);
    if (response.status === HTTPStatusCodes.Ok || response.status === HTTPStatusCodes.Created) {
      dispatch(
        showAlert({
          severity: 'success',
          title: `${DRAWER_TITLES_MAP[drawerVariant]} Call`,
          body: `The ${DRAWER_TITLES_MAP[drawerVariant]} Call was ${ACTION_VERB} succesfully`
        })
      );
      setDrawerVariant(null);
    } else {
      dispatch(
        showAlert({
          severity: 'error',
          title: DRAWER_TITLES_MAP[drawerVariant],
          autoHideDuration: 5000,
          body: `Something went wrong!`
        })
      );
    }
  };

  const form = useForm();
  const { handleSubmit, reset, watch } = form;

  const isLoading =
    formFetchState.status === UIStatus.Loading ||
    detailsOverviewFetchState.status === UIStatus.Loading;

  useEffect(() => {
    if (formFetchState.results) {
      const defaultValues = formatDefaultValues({
        data: formFetchState.results
      });
      reset(defaultValues);
    }
  }, [reset, formFetchState.results]);

  const isFormPreloaded =
    formFetchState.status === UIStatus.Success && Object.keys(watch()).length > 1;
  const isFormReady = isFormPreloaded || formFetchState.status === UIStatus.Error; // backend throws 404 if no interviews has been created

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: onClose
      }
    },
    {
      props: {
        initialText: strings.shared.ui.save,
        variant: 'contained',
        type: 'submit',
        disabled: isSaving || isLoading,
        onProgressText: strings.shared.ui.saving,
        isSaving,
        onClick: handleSubmit(onSave)
      },
      mode: 'save'
    }
  ];

  return (
    <FPDrawer
      CardHeaderProps={{ title: DRAWER_TITLES_MAP[drawerVariant] }}
      FPDrawerProps={{
        open,
        id: 'drawer-form'
      }}
      onClose={onClose}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <StatusManager status={isSaving || isLoading ? UIStatus.Loading : UIStatus.Success}>
        {isFormReady && (
          <>
            <DetailStatus
              loading={<Loader />}
              success={details => <DetailsOverview detailsOverviewFetchState={details} />}
            />
            <Divider />
            <FormContext {...form}>{getContentToRender({ drawerVariant })}</FormContext>
          </>
        )}
      </StatusManager>
    </FPDrawer>
  );
};
